import React, { useState, useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import {
    Button,
    ConfigProvider,
    Divider,
    Flex,
    Spin,
    Splitter,
    Typography,
    Row,
    Col,
} from '@app/ui';
import { User } from '@app/types/api';
import { ArrowLeftOutlined } from '@ant-design/icons';

import sportsApi from '@app/utils/sportsApi';
import connect from '@app/utils/connect';
import userActions from '@app/actions/user';
import ReloLogo from '../login/images/relo-edge-logo.svg';
import ReloTransparentLogo from '../login/images/LogoTransparent.svg';
import ErrorBoundary from '@app/ErrorBoundary';
import CrashedComponent from '@app/components/errors/CrashedComponent';

import ResetPasswordForm from './ResetPasswordForm';
import '../login/styles/Login.scss';

type ResetPasswordProps = {
    user: User;
    login: (credentials: { email: string; password: string }) => void;
};

const ResetPassword = ({ user, login }: ResetPasswordProps) => {
    const [sizes, setSizes] = useState<(number | string)[]>(['50%', '50%']);
    const [userId, setUserId] = useState(null);
    const [userEmail, setUserEmail] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isTokenValid, setIsTokenValid] = useState(false);
    const [formMessage, setFormMessage] = useState({
        type: null,
        text: null,
    });
    const [errorMessage, setErrorMessage] = useState(null);

    const location = useLocation(); // <-- use the hook to get the location object
    const navigate = useNavigate();

    useEffect(() => {
        _checkTokenValidity();
    }, []);

    const _checkTokenValidity = () => {
        const searchParams = new URLSearchParams(location.search); // <-- use location.search
        const token = searchParams.get('token') || '';

        const payload = {
            action: 'verifyPasswordResetToken',
            resetPasswordRequestToken: token,
            domainObject: {},
        };

        sportsApi
            .url('/users')
            .json(payload)
            .post()
            .badRequest(error => {
                const response = JSON.parse(error.message);
                setIsLoading(false);
                setIsTokenValid(false);
                setErrorMessage(response.message);
            })
            .json(json => {
                setIsLoading(false);
                setIsTokenValid(true);
                setUserId(json.id);
                setUserEmail(json.email);
            });
    };

    const _submitForm = values =>
        new Promise<void>(res => {
            const searchParams = new URLSearchParams(location.search); // <-- use location.search
            const token = searchParams.get('token') || '';

            const payload = {
                action: 'changeMvcUserPassword',
                resetPasswordRequestToken: token,
                domainObject: {
                    id: userId,
                    password: values.password,
                },
            };

            sportsApi
                .url('/users')
                .json(payload)
                .post()
                .badRequest(error => {
                    const response = JSON.parse(error.message);
                    setFormMessage({
                        type: 'dan',
                        text: response.message,
                    });
                    res();
                })
                .res(() => {
                    setFormMessage({
                        type: 'suc',
                        text: 'Password set, you will be logged in in a few seconds.',
                    });

                    setTimeout(
                        () =>
                            login({
                                email: userEmail,
                                password: values.password,
                            }),
                        3000,
                    );
                });
        });

    const { isLoggedIn, isSelectingClient } = user;

    if (isLoggedIn) {
        return <Navigate to="/" />;
    }

    if (isSelectingClient) {
        return <Navigate to="/login" />;
    }

    return (
        <ErrorBoundary FallbackComponent={CrashedComponent}>
            <ConfigProvider
                theme={{
                    token: {
                        colorPrimary: 'rgba(0, 159, 223, 1)',
                        colorLink: 'rgba(0, 159, 223, 1)',
                        colorError: 'rgba(212, 15, 125, 1)',
                    },
                    components: {
                        Typography: {
                            fontFamily: 'Roboto',
                            fontSize: 16,
                            colorText: 'rgba(0, 0, 0, 0.65)',
                            fontSizeHeading1: 30,
                            colorTextHeading: 'rgba(0, 0, 0, 0.88)',
                        },
                        Input: {
                            colorBorder: 'rgba(0, 0, 0, 0.15)',
                            colorText: 'rgba(0, 0, 0, 0.88)',
                            inputFontSize: 14,
                            colorIcon: 'rgba(0, 0, 0, 0.45)',
                        },
                        Divider: {
                            lineWidth: 1,
                            colorBorder: 'rgba(0, 0, 0, 0.25)',
                        },
                    },
                }}
            >
                <Flex
                    vertical
                    id="main"
                    role="main"
                    justify="center"
                    align="center"
                    className="-login-container -full-height"
                >
                    <Row gutter={16} style={{ width: '100%' }}>
                        <Col md={24}>
                            <Flex justify="center" align="center">
                                <div id="messages" />
                                <div
                                    className="modal"
                                    id="mainModal"
                                    role="dialog"
                                    aria-labelledby="mainModalLabel"
                                >
                                    <div className="modal-dialog" />
                                </div>
                                <Splitter onResize={setSizes}>
                                    <Splitter.Panel
                                        size={sizes[0]}
                                        resizable={false}
                                    >
                                        <Flex
                                            className="-login-side-panel -full-height"
                                            justify="center"
                                            align="center"
                                        >
                                            <a
                                                href="https://www.relometrics.com"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                <img
                                                    className="logo"
                                                    src={ReloTransparentLogo}
                                                />
                                            </a>
                                        </Flex>
                                    </Splitter.Panel>

                                    <Splitter.Panel
                                        size={sizes[1]}
                                        className="-form-container"
                                    >
                                        <Flex
                                            align="center"
                                            vertical
                                            className="-welcome"
                                        >
                                            <a
                                                href="https://www.relometrics.com"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                <img
                                                    className="logo"
                                                    src={ReloLogo}
                                                />
                                            </a>

                                            <section className="section section-1 -m-t-2">
                                                <Typography.Title>
                                                    Welcome Back
                                                </Typography.Title>
                                                <Typography.Text>
                                                    Login to your Relo EDGE
                                                    dashboard
                                                </Typography.Text>
                                            </section>
                                        </Flex>

                                        {!isLoading && isTokenValid && (
                                            <ResetPasswordForm
                                                onSubmit={_submitForm}
                                                message={formMessage}
                                            />
                                        )}
                                        {!isLoading && !isTokenValid && (
                                            <Flex
                                                align="start"
                                                vertical
                                                className="-m-t-3"
                                            >
                                                <div className="gds-well forgot-password-well">
                                                    <Typography.Text>
                                                        {errorMessage}
                                                    </Typography.Text>
                                                </div>
                                                <Divider />

                                                <Button
                                                    onClick={() =>
                                                        navigate('/login')
                                                    }
                                                    type="link"
                                                    icon={<ArrowLeftOutlined />}
                                                    iconPosition="start"
                                                >
                                                    Back to Sign In
                                                </Button>
                                            </Flex>
                                        )}
                                        {isLoading && (
                                            <Flex
                                                flex={1}
                                                align="center"
                                                justify="center"
                                                className="-m-t-3"
                                            >
                                                <Spin size="large" />
                                            </Flex>
                                        )}
                                    </Splitter.Panel>
                                </Splitter>
                            </Flex>
                        </Col>
                    </Row>
                </Flex>
            </ConfigProvider>
        </ErrorBoundary>
    );
};

export default connect(['user'], userActions)(ResetPassword);
